import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { useLoggedinUser, useService } from '@hooks';
import { RxUtils, Tr } from '@utils';
import { isPermissionVisible, Permission, roleService, userInvitationService } from '@services';
import { Grid, MenuItem, Stack } from '@mui/material';
import { FormStepGreySection, FormStepLayout, FormStepQuestion, PermissionCheckbox, PortalPageTitle } from '@components/portal/common';
import { FormikTextfield } from '@components/common';

const trPathCollaborator = (key: string) => `collaborators.add-edit-collaborator.${key}`;

type FormFields = Extendable<{
	email: string;
	name: string;
	surname: string;
	roleId: number;
	roleName: string;
	permissions: Permission[];
}>;

export const AddCollaborator: FC = () => {
	const navigate = useNavigate();
	const user = useLoggedinUser();

	const { t } = useTranslation('portal');
	const { data: roles } = useService(() => roleService.getAll(), []);
	const formik = useFormik<FormFields>({
		initialValues: { email: '', name: '', surname: '', roleId: '' },
		enableReinitialize: true,
		validateOnChange: true,
		validationSchema: yup.object({
			email: yup.string().email(t('email-invalid')).required(t('field-required')),
			name: yup.string().required(t('field-required')),
			surname: yup.string().required(t('field-required')),
			roleId: yup.number().required(t('field-required')),
			roleName: yup
				.string()
				.when('roleId', { is: '-1', then: yup.string().required(t('field-required')), otherwise: yup.string().nullable() })
		}),
		onSubmit: ({ email, name, surname, roleId, roleName, permissions }, api) => {
			Promise.resolve(roleId)
				.then((id) => (id < 0 ? lastValueFrom(roleService.create({ name: roleName, permissions }).pipe(map((r) => r.id))) : id))
				.then((id) => lastValueFrom(userInvitationService.invite({ email, name, surname, roleId: id })))
				.catch(RxUtils.noop)
				.then(() => {
					api.setSubmitting(false);
					navigate('..');
				});
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;

	return (
		<Stack direction="column" sx={{ maxHeight: 1.0, height: 1.0, flexGrow: 1, paddingY: 5, overflow: 'hidden' }}>
			<PortalPageTitle
				paddingX={3}
				title={<Tr.Portal path={trPathCollaborator('add-title')} />}
				subtitle={<Tr.Portal path={trPathCollaborator('add-subtitle')} />}
				onBackButtonClick={() => navigate(-1)}
			/>
			<FormStepLayout
				primaryButtonProps={{
					disabled,
					onClick: submitForm,
					children: <Tr.Portal path="complete" />,
					'data-testid': 'add-collaborator-next-button'
				}}
				hideSecondaryButton
			>
				<form onSubmit={handleSubmit}>
					<Stack direction="column">
						<FormStepQuestion title={<Tr.Portal path={trPathCollaborator('user-details-questions')} />}>
							<Grid container columnSpacing={6} rowSpacing={4}>
								<Grid item xs={4}>
									<FormikTextfield
										formikApi={formik}
										name="name"
										title={t('collaborators.first-name')}
										placeholder={t('collaborators.first-name')}
										required
									/>
								</Grid>
								<Grid item xs={4}>
									<FormikTextfield
										formikApi={formik}
										name="surname"
										title={t('collaborators.last-name')}
										placeholder={t('collaborators.last-name')}
										required
									/>
								</Grid>
								<Grid item xs={4}>
									<FormikTextfield
										formikApi={formik}
										name="email"
										title={t('collaborators.email')}
										placeholder={t('collaborators.email')}
										required
									/>
								</Grid>
								<Grid item xs={4}>
									<FormikTextfield
										select
										formikApi={formik}
										name="roleId"
										title={t('collaborators.role')}
										placeholder={t('collaborators.role')}
										required
									>
										{(roles || [])
											.filter((role) => user?.canManageRole(role))
											.map(({ id, name }) => (
												<MenuItem key={`role-${name}-${id}`} value={id}>
													{name}
												</MenuItem>
											))}
										<MenuItem key="role-other" value={-1}>
											<Tr.Portal path="other" />
										</MenuItem>
									</FormikTextfield>
								</Grid>
							</Grid>
						</FormStepQuestion>
						{formik.values.roleId < 0 ? (
							<FormStepGreySection>
								<Stack direction="column" spacing={4}>
									<FormStepQuestion title={<Tr.Portal path={trPathCollaborator('assign-role-name')} />}>
										<Grid container columnSpacing={6} rowSpacing={4}>
											<Grid item xs={4}>
												<FormikTextfield
													formikApi={formik}
													name="roleName"
													placeholder={t('collaborators.role-name')}
													required
												/>
											</Grid>
										</Grid>
									</FormStepQuestion>
									<FormStepQuestion title={<Tr.Portal path={trPathCollaborator('assign-role-capabilities')} />}>
										<Grid container columnSpacing={2}>
											{Object.values(Permission)
												.filter(isPermissionVisible)
												.map((permission, i) => (
													<Grid item xs={4} key={`permission-${permission}-${i}`}>
														<PermissionCheckbox formikApi={formik} name="permissions" value={permission} />
													</Grid>
												))}
										</Grid>
									</FormStepQuestion>
								</Stack>
							</FormStepGreySection>
						) : null}
					</Stack>
				</form>
			</FormStepLayout>
		</Stack>
	);
};
